import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { EventPageData } from '../data/eventContent';
import ParallaxHero from '../components/ParallaxHero';
import FeaturedGallery from '../components/FeaturedGallery';

const menuSections = [
  { id: 'section-plus',          label: 'PLUS+',        sub: 'Premium menu\'s',        num: '01' },
  { id: 'section-party',         label: 'PARTY',        sub: 'Party & events',          num: '02' },
  { id: 'section-kids',          label: 'KIDS',         sub: 'Voor de kleintjes',       num: '03' },
  { id: 'section-options',       label: 'OPTIONS',      sub: 'Aperitief & extra\'s',    num: '04' },
  { id: 'section-dessert',       label: 'DESSERT',      sub: 'Huisgemaakte desserts',   num: '05' },
  { id: 'section-springkasteel', label: 'SPRINGKASTEEL', sub: 'Extra service',          num: '06' },
];

const scrollToSection = (id: string) => {
  const el = document.getElementById(id);
  if (el) {
    const top = el.getBoundingClientRect().top + window.scrollY - 110;
    window.scrollTo({ top, behavior: 'smooth' });
  }
};

const EventPageTemplate: React.FC<EventPageData> = (data) => {
  const isKids = data.id === 'kids';
  const isWeddings = data.id === 'weddings';
  const visibleSections = isKids
    ? menuSections.filter(s => s.id !== 'section-plus' && s.id !== 'section-party')
    : isWeddings
      ? menuSections
      : menuSections.filter(s => s.id !== 'section-springkasteel');
  const sectionNum = (id: string) => {
    const idx = visibleSections.findIndex(s => s.id === id);
    return idx >= 0 ? String(idx + 1).padStart(2, '0') : '';
  };
  const [activeSection, setActiveSection] = useState<string>(isKids ? 'section-kids' : 'section-plus');

  useEffect(() => {
    const handleScroll = () => {
      const offset = 160;
      let current = visibleSections[0].id;
      for (const { id } of visibleSections) {
        const el = document.getElementById(id);
        if (el && el.getBoundingClientRect().top <= offset) {
          current = id;
        }
      }
      setActiveSection(current);
    };
    window.addEventListener('scroll', handleScroll, { passive: true });
    handleScroll();
    return () => window.removeEventListener('scroll', handleScroll);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    const hash = window.location.hash;
    if (!hash) return;
    const id = hash.replace('#', '');
    const attempt = (tries: number) => {
      const el = document.getElementById(id);
      if (el) {
        const top = el.getBoundingClientRect().top + window.scrollY - 110;
        window.scrollTo({ top, behavior: 'smooth' });
        setActiveSection(id);
      } else if (tries > 0) {
        setTimeout(() => attempt(tries - 1), 150);
      }
    };
    attempt(10);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  type FormStatus = 'idle' | 'loading' | 'success' | 'error';
  const [formStatus, setFormStatus] = React.useState<FormStatus>('idle');

  const setDutchValidity = (e: React.FormEvent<any>) => {
    const el = e.target as HTMLInputElement;
    if (el.validity.valueMissing) el.setCustomValidity('Dit veld is verplicht.');
    else if (el.validity.typeMismatch) el.setCustomValidity('Vul een geldig e-mailadres in.');
    else el.setCustomValidity('');
  };
  const clearValidity = (e: React.ChangeEvent<any>) => {
    (e.target as HTMLInputElement).setCustomValidity('');
  };

  const handlePrivateFormSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setFormStatus('loading');
    try {
      const formData = new FormData(e.currentTarget);
      const res = await fetch('/', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: new URLSearchParams(formData as any).toString(),
      });
      if (res.ok) {
        setFormStatus('success');
        (e.target as HTMLFormElement).reset();
      } else {
        setFormStatus('error');
      }
    } catch {
      setFormStatus('error');
    }
  };

  return (
    <div className="bg-white">
      {data.id !== 'private-parties' && (
        <ParallaxHero
          image={data.heroImage}
          title={data.heroTitle || 'Menu'}
          subtitle={data.heroSubtitle}
          smallTitle={!!data.heroTitle}
        />
      )}

      {data.id !== 'private-parties' && (
      <section className="bg-friet-beige py-14 md:py-20">
        <div className="container mx-auto px-4">
          <div className="max-w-3xl mx-auto text-center">
            {!data.heroTitle && data.id !== 'weddings' && (
            <h1 className="inline-block border-b-2 border-friet-red pb-1 mb-8">
              <span className="font-script font-bold text-friet-red text-3xl md:text-5xl">
                {data.title} {data.subtitle}
              </span>
            </h1>
            )}
            <div className="flex justify-center mb-8">
              <div className="flex items-center gap-3 text-friet-red/30">
                <div className="w-12 h-px bg-friet-red/30"></div>
                <i className="fas fa-utensils text-xs"></i>
                <div className="w-12 h-px bg-friet-red/30"></div>
              </div>
            </div>
            <p className="text-base sm:text-lg md:text-xl leading-loose font-serif text-friet-oak/80 whitespace-pre-line">
              {data.description}
            </p>
          </div>
        </div>
      </section>
      )}

      {/* New White Zone Section */}
      {data.id !== 'special-occasions' && data.id !== 'private-parties' && data.id !== 'corporate' && (<>
      <section className="bg-white pt-12 pb-16">
        <div className="container mx-auto px-4">
          {/* Mobile sticky tab bar */}
          <div className="md:hidden sticky top-16 z-20 bg-white/95 backdrop-blur-sm border-b border-friet-red/10 -mx-4 px-2 mb-6 flex overflow-x-auto scrollbar-hide">
            {visibleSections.map(({ id, label }) => {
              const isActive = activeSection === id;
              return (
                <button
                  key={id}
                  onClick={() => scrollToSection(id)}
                  className={[
                    'flex-shrink-0 px-4 py-3 font-script font-bold text-sm border-b-2 transition-all duration-300 whitespace-nowrap',
                    isActive ? 'text-friet-red border-friet-red' : 'text-friet-black/40 border-transparent',
                  ].join(' ')}
                >
                  {label}
                </button>
              );
            })}
          </div>

          <div className="flex flex-col md:flex-row items-start justify-center gap-8 md:gap-16">
            {/* Desktop sticky left nav */}
            <div className="hidden md:block w-full md:w-5/12 sticky top-24 self-start">
              <p className="font-serif text-xs uppercase tracking-widest text-friet-red/50 mb-1 text-right">Onze</p>
              <h2 className="font-serif text-3xl text-friet-black mb-8 text-right">Formules</h2>

              <nav className="relative flex flex-col">
                {/* Vertical connector line */}
                <div className="absolute right-[1.19rem] top-5 bottom-5 w-px bg-friet-red/12 pointer-events-none" />

                {visibleSections.map(({ id, label, sub, num }, idx) => {
                  const isActive = activeSection === id;
                  const activeIdx = visibleSections.findIndex(s => s.id === activeSection);
                  const isPast = idx < activeIdx;

                  return (
                    <button
                      key={id}
                      onClick={() => scrollToSection(id)}
                      className="flex items-center gap-5 text-right justify-end py-3 group"
                    >
                      {/* Text block */}
                      <div className="flex flex-col items-end">
                        <span
                          className={[
                            'font-script font-bold leading-none transition-all duration-500',
                            isActive
                              ? 'text-friet-red text-4xl'
                              : 'text-friet-black/30 text-2xl group-hover:text-friet-red/55 group-hover:text-3xl',
                          ].join(' ')}
                        >
                          {label}
                        </span>
                        <span
                          className={[
                            'font-serif text-xs transition-all duration-500 mt-0.5',
                            isActive ? 'text-friet-red/55 opacity-100 max-h-6' : 'opacity-0 max-h-0 overflow-hidden',
                          ].join(' ')}
                        >
                          {sub}
                        </span>
                      </div>

                      {/* Step circle */}
                      <div
                        className={[
                          'relative z-10 w-10 h-10 rounded-full flex-shrink-0 flex items-center justify-center border-2 transition-all duration-500',
                          isActive
                            ? 'border-friet-red bg-friet-red text-white scale-110'
                            : isPast
                            ? 'border-friet-red/40 bg-friet-red/10 text-friet-red/60'
                            : 'border-friet-red/15 bg-white text-friet-black/25 group-hover:border-friet-red/40 group-hover:text-friet-red/50',
                        ].join(' ')}
                      >
                        {isPast && !isActive
                          ? <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}><path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" /></svg>
                          : <span className="font-serif text-xs font-bold">{String(idx + 1).padStart(2, '0')}</span>
                        }
                      </div>
                    </button>
                  );
                })}
              </nav>
            </div>

            <div className="w-full md:w-7/12 text-center md:text-left border-l-0 md:border-l md:border-friet-red/10 md:pl-12">
              
              <div id="section-plus" className="grid grid-cols-1 gap-8 text-left max-w-xl mx-auto md:mx-0">
                {!isKids && (<>
                <div className="flex items-center gap-3 -mt-2 mb-2">
                  <span className="text-sm font-serif uppercase tracking-widest text-friet-red bg-friet-red/8 px-3 py-1 rounded-full">{sectionNum('section-plus')} — Plus+</span>
                  <div className="flex-1 h-px bg-friet-red/10" />
                </div>
                <div>
                  <div className="flex justify-between items-baseline mb-2">
                    <h3 className="font-serif font-bold text-xl text-friet-red">Burgermenu</h3>
                    <span className="text-friet-red font-bold">€ 17,50</span>
                  </div>
                  <p className="text-gray-600 text-sm leading-relaxed">
                    Loaded Home Made Hamburger naar keuze (keuze uit 3 hamburgers op de kaart)<br />
                    Krokante verse frieten<br />
                    Frietsausje
                  </p>
                </div>

                <div>
                  <div className="flex justify-between items-baseline mb-2">
                    <h3 className="font-serif font-bold text-xl text-friet-red">Homemade menu</h3>
                    <span className="text-friet-red font-bold">€ 18,00</span>
                  </div>
                  <p className="text-gray-600 text-sm leading-relaxed">
                    Home made Stoofvlees / Balletjes in Tomatensaus / Vol-au-vent<br />
                    Krokante verse frieten<br />
                    Frietsausje
                  </p>
                </div>

                <div>
                  <div className="flex justify-between items-baseline mb-2">
                    <h3 className="font-serif font-bold text-xl text-friet-red">Scampiplus menu</h3>
                    <span className="text-friet-red font-bold">€ 17,00</span>
                  </div>
                  <p className="text-gray-600 text-sm leading-relaxed">
                    Scampi Brochette<br />
                    Krokante verse frieten<br />
                    Frietsausje
                  </p>
                </div>

                <div>
                  <div className="flex justify-between items-baseline mb-2">
                    <h3 className="font-serif font-bold text-xl text-friet-red">À la minute menu</h3>
                    <span className="text-friet-red font-bold">€ 17,00</span>
                  </div>
                  <p className="text-gray-600 text-sm leading-relaxed">
                    Steak Tartaar<br />
                    Krokante verse frieten<br />
                    Frietsausje
                  </p>
                </div>

                <div>
                  <div className="flex justify-between items-baseline mb-2">
                    <h3 className="font-serif font-bold text-xl text-friet-red">Cheesy menu</h3>
                    <span className="text-friet-red font-bold">€ 16,00</span>
                  </div>
                  <p className="text-gray-600 text-sm leading-relaxed">
                    2 artisanale kaaskroketten<br />
                    Salad bar<br />
                    Krokante verse frieten<br />
                    Frietsausje
                  </p>
                </div>
                </>)}

                {!isKids && (
                <div id="section-party" className="pt-12 mt-4">
                  <div className="flex items-center gap-3 mb-6">
                    <span className="text-sm font-serif uppercase tracking-widest text-friet-red bg-friet-red/8 px-3 py-1 rounded-full">{sectionNum('section-party')} — Party</span>
                    <div className="flex-1 h-px bg-friet-red/10" />
                  </div>
                  <div className="space-y-8">
                    <div>
                      <div className="flex justify-between items-baseline mb-2">
                        <h3 className="font-serif font-bold text-xl text-friet-red">Party + menu</h3>
                        <span className="text-friet-red font-bold">€ 16,00</span>
                      </div>
                      <p className="text-gray-600 text-sm leading-relaxed">
                        Slim hamburger<br />
                        Frituursnack (keuze uit 4 snacks op de kaart)<br />
                        Krokante verse frieten<br />
                        Frietsausje
                      </p>
                    </div>

                    <div>
                      <div className="my-2 text-friet-red font-serif italic text-center md:text-left opacity-60">of</div>
                      <div className="flex justify-between items-baseline mb-2">
                        <h3 className="font-serif font-bold text-xl text-friet-red">Party + menu</h3>
                        <span className="text-friet-red font-bold">€ 14,50</span>
                      </div>
                      <p className="text-gray-600 text-sm leading-relaxed">
                        Frituursnacks 2 stuks pp (keuze uit 4 snacks op de kaart)<br />
                        Krokante verse frieten<br />
                        Frietsausje
                      </p>
                    </div>

                    <div>
                      <div className="flex justify-between items-baseline mb-2">
                        <h3 className="font-serif font-bold text-xl text-friet-red">Party menu</h3>
                        <span className="text-friet-red font-bold">€ 12,50</span>
                      </div>
                      <p className="text-gray-600 text-sm leading-relaxed">
                        Slim hamburger of frituursnack (keuze uit 4 snacks op de kaart)<br />
                        Krokante verse frieten<br />
                        Frietsausje
                      </p>
                    </div>
                  </div>
                </div>
                )}

                    <div id="section-kids" className="pt-12 mt-4">
                      <div className="flex items-center gap-3 mb-6">
                        <span className="text-sm font-serif uppercase tracking-widest text-friet-red bg-friet-red/8 px-3 py-1 rounded-full">{sectionNum('section-kids')} — Kids</span>
                        <div className="flex-1 h-px bg-friet-red/10" />
                      </div>
                      <div className="space-y-6">
                        <div>
                          <div className="flex justify-between items-baseline mb-1">
                            <h4 className="font-serif font-bold text-xl text-friet-red">Kids menu</h4>
                            <span className="text-friet-red font-bold">€ 9,00</span>
                          </div>
                          <p className="text-gray-600 text-sm leading-relaxed">
                            Frituursnack for kids<br />
                            Krokante verse frieten<br />
                            Frietsausje
                          </p>
                        </div>
                        <div className="flex items-center gap-4">
                          <div className="h-px bg-friet-red/10 flex-grow"></div>
                          <span className="font-serif italic text-gray-400 text-sm">of</span>
                          <div className="h-px bg-friet-red/10 flex-grow"></div>
                        </div>
                        <div>
                          <div className="flex justify-between items-baseline mb-1">
                            <h4 className="font-serif font-bold text-xl text-friet-red">Kids menu</h4>
                            <span className="text-friet-red font-bold">€ 9,00</span>
                          </div>
                          <p className="text-gray-600 text-sm leading-relaxed">
                            Slim hamburger for kids<br />
                            Krokante versie frieten<br />
                            Frietsausje
                          </p>
                        </div>
                      </div>
                    </div>

                    <div id="section-options" className="pt-12 mt-4">
                      <div className="flex items-center gap-3 mb-6">
                        <span className="text-sm font-serif uppercase tracking-widest text-friet-red bg-friet-red/8 px-3 py-1 rounded-full">{sectionNum('section-options')} — Options</span>
                        <div className="flex-1 h-px bg-friet-red/10" />
                      </div>
                      
                      <h4 className="font-serif font-bold text-xl text-friet-red mt-6 mb-3">Aperitief</h4>
                      
                      <div className="mb-6">
                        <div className="flex justify-between items-start group mb-1">
                          <span className="font-bold text-gray-800">Party</span>
                          <span className="text-friet-red font-bold">€ 4,00</span>
                        </div>
                        <p className="text-gray-600 text-sm leading-relaxed">
                          Loempia's met zoetzuur sausje 2 st<br />
                          Kaasballetjes met truffelmayonaise 2 st<br />
                          Bitterballen met mosterdsausje 2 stuks
                        </p>
                      </div>

                      <div className="mb-6">
                        <div className="flex justify-between items-start group mb-1">
                          <span className="font-bold text-gray-800">Plus +</span>
                          <span className="text-friet-red font-bold">€ 5,00</span>
                        </div>
                        <p className="text-gray-600 text-sm leading-relaxed">
                          Loempia's met zoetzuur sausje 2 st<br />
                          Kaasballetjes met truffelmayonaise of bitterballen met mosterdsausje 2 st<br />
                          Scampi's met dipsausje 2 st<br />
                          Groententaartje
                        </p>
                      </div>

                      <div className="mb-6">
                        <div className="flex justify-between items-start group mb-1">
                          <span className="font-bold text-gray-800">Plus + delux</span>
                          <span className="text-friet-red font-bold">€ 8,00</span>
                        </div>
                        <p className="text-gray-600 text-sm leading-relaxed">
                          Steak tartaar of zalmtartaar<br />
                          Scampi's met dipsausje 2 st<br />
                          Loempia's met zoetzuur sausje 2 st<br />
                          Groententaartje
                        </p>
                      </div>

                      <div className="flex justify-between items-center group border-t border-friet-red/10 pt-4">
                        <span className="font-bold text-gray-800">Salade</span>
                        <span className="text-friet-red font-bold">€ 4,00</span>
                      </div>

                    </div>

                    <div id="section-dessert" className="pt-12 mt-4">
                      <div className="flex items-center gap-3 mb-6">
                        <span className="text-sm font-serif uppercase tracking-widest text-friet-red bg-friet-red/8 px-3 py-1 rounded-full">{sectionNum('section-dessert')} — Dessert</span>
                        <div className="flex-1 h-px bg-friet-red/10" />
                      </div>
                      {isKids ? (
                        <>
                          <div className="flex justify-between items-baseline mb-2">
                            <h3 className="font-serif font-bold text-xl text-friet-red">Homemade Dessert for kids</h3>
                            <span className="text-friet-red font-bold">€ 4,00</span>
                          </div>
                          <p className="text-gray-600 text-sm leading-relaxed">
                            Chocomousse<br />
                            Tiramisu met Speculaas
                          </p>
                          <div className="flex justify-between items-baseline mt-4 mb-2">
                            <h3 className="font-serif font-bold text-xl text-friet-red">Homemade chocoladecake</h3>
                            <span className="text-friet-red font-bold">€ 3,50</span>
                          </div>
                          <p className="text-gray-600 text-sm leading-relaxed">Chocoladecake met extra laagje chocolade</p>
                        </>
                      ) : (
                        <>
                          <div className="flex justify-between items-baseline mb-2">
                            <h3 className="font-serif font-bold text-xl text-friet-red">Homemade Dessert</h3>
                            <span className="text-friet-red font-bold">€ 5,50</span>
                          </div>
                          <p className="text-gray-600 text-sm leading-relaxed">
                            Chocomousse<br />
                            Tiramisu met Speculaas
                          </p>
                          <div className="flex justify-between items-baseline mt-4 mb-2">
                            <h3 className="font-serif font-bold text-xl text-friet-red">Homemade Dessert for kids</h3>
                            <span className="text-friet-red font-bold">€ 4,00</span>
                          </div>
                          <p className="text-gray-600 text-sm leading-relaxed">
                            Chocomousse<br />
                            Tiramisu met Speculaas
                          </p>
                          <div className="flex justify-between items-baseline mt-4 mb-2">
                            <h3 className="font-serif font-bold text-xl text-friet-red">Homemade chocoladecake</h3>
                            <span className="text-friet-red font-bold">€ 3,50</span>
                          </div>
                          <p className="text-gray-600 text-sm leading-relaxed">Chocoladecake met extra laagje chocolade</p>
                        </>
                      )}
                    </div>

                    {(isKids || isWeddings) && (
                    <div id="section-springkasteel" className="pt-12 mt-4">
                      <div className="flex items-center gap-3 mb-6">
                        <span className="text-sm font-serif uppercase tracking-widest text-friet-red bg-friet-red/8 px-3 py-1 rounded-full">{sectionNum('section-springkasteel')} — Springkasteel</span>
                        <div className="flex-1 h-px bg-friet-red/10" />
                      </div>
                      <div className="flex justify-between items-baseline mb-2">
                        <h3 className="font-serif font-bold text-xl text-friet-red">Springkasteel</h3>
                        <span className="text-friet-red font-bold">€ 125</span>
                      </div>
                      <p className="text-gray-600 text-sm leading-relaxed">Thema: dieren</p>
                      <div className="mt-3 text-left">
                        <p className="text-xs text-friet-oak/50 font-serif italic">* prijzen per persoon en inclusief BTW</p>
                        <p className="text-xs text-friet-oak/50 font-serif italic">* frietjes zijn steeds à la volenté</p>
                      </div>
                    </div>
                    )}

              </div>
            </div>
          </div>
        </div>
      </section>


      </>)}

      {/* Section Title */}
      {data.sectionTitle && data.id !== 'special-occasions' && (
        <section className="py-16 bg-friet-beige/30">
          <div className="max-w-4xl mx-auto px-4 text-center">
            <h2 className="font-script font-bold text-4xl md:text-5xl text-friet-red">
              {data.sectionTitle}
            </h2>
            <div className="w-24 h-1 bg-friet-gold mx-auto mt-4"></div>
          </div>
        </section>
      )}

      {/* Story Section */}
      {data.storyTitle && data.id !== 'special-occasions' && data.id !== 'private-parties' && data.id !== 'corporate' && !isKids && (
        <section className="py-24 px-4 bg-white">
          <div className="max-w-4xl mx-auto text-center">
            <h2 className="font-serif text-4xl md:text-5xl text-friet-black mb-6">
              {data.storyTitle}
              <span className="text-friet-gold font-script ml-2"> {data.storySubtitle}</span>
            </h2>
            <div className="prose prose-xl max-w-none text-gray-700 leading-relaxed italic">
              {data.storyContent}
            </div>
          </div>
        </section>
      )}

      {/* Story Section 2 */}
      {data.storyTitle2 && data.id !== 'private-parties' && (
        <section className="py-24 px-4 bg-friet-beige/20">
          <div className="max-w-4xl mx-auto text-center">
            <h2 className="font-serif text-4xl md:text-5xl text-friet-black mb-6">
              {data.storyTitle2}
              <span className="text-friet-gold font-script ml-2"> {data.storySubtitle2}</span>
            </h2>
            <div className="prose prose-xl max-w-none text-gray-700 leading-relaxed italic">
              {data.storyContent2}
            </div>
          </div>
        </section>
      )}

      {/* Why Section 2 */}
      {data.whyFeatures2 && (
        <section className="py-20 bg-friet-gold/5">
          <div className="max-w-7xl mx-auto px-4">
            <h2 className="font-serif text-4xl text-center mb-16 text-friet-black">{data.whyTitle2}</h2>
            <div className="grid md:grid-cols-3 gap-12">
              {data.whyFeatures2.map((feature, i) => (
                <div key={i} className="text-center group">
                  <div className="w-24 h-24 mx-auto mb-6 bg-white rounded-full flex items-center justify-center shadow-lg group-hover:scale-110 transition-transform">
                    {feature.icon.startsWith('fa') ? (
                      <i className={`${feature.icon} text-4xl text-friet-gold`}></i>
                    ) : (
                      <span className="text-5xl">{feature.icon}</span>
                    )}
                  </div>
                  <h3 className="font-serif text-2xl mb-4 text-friet-black">{feature.title}</h3>
                  <p className="text-gray-600 leading-relaxed">{feature.description}</p>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Why Section */}
      {data.whyFeatures && (
        <section className={`py-20 ${data.id === 'weddings' ? 'bg-friet-gold/5' : 'bg-friet-beige/30'}`}>
          <div className="max-w-7xl mx-auto px-4">
            {data.id !== 'corporate' && <h2 className="font-serif text-4xl text-center mb-16 text-friet-black">{data.whyTitle}</h2>}
            {data.id === 'corporate' ? (
              <div className="grid md:grid-cols-3 gap-8">
                {data.whyFeatures.map((feature, i) => (
                  <div key={i} className="rounded-3xl shadow-xl hover:translate-y-[-5px] transition-transform overflow-hidden">
                    <div className="bg-friet-beige p-6 flex flex-col items-center gap-3">
                      <div className="w-14 h-14 bg-white rounded-full flex items-center justify-center shadow">
                        {feature.icon.startsWith('fa') ? (
                          <i className={`${feature.icon} text-2xl text-friet-gold`}></i>
                        ) : (
                          <span className="text-3xl">{feature.icon}</span>
                        )}
                      </div>
                      <h3 className="font-serif text-xl text-friet-red text-center">{feature.title}</h3>
                    </div>
                    <div className="bg-white p-5">
                      <p className="text-gray-600 leading-relaxed text-sm">{feature.description}</p>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="grid md:grid-cols-3 gap-12">
                {data.whyFeatures.map((feature, i) => (
                  <div key={i} className="text-center group">
                    <div className="w-24 h-24 mx-auto mb-6 bg-white rounded-full flex items-center justify-center shadow-lg group-hover:scale-110 transition-transform">
                      {feature.icon.startsWith('fa') ? (
                        <i className={`${feature.icon} text-4xl text-friet-gold`}></i>
                      ) : (
                        <span className="text-5xl">{feature.icon}</span>
                      )}
                    </div>
                    <h3 className="font-serif text-2xl mb-4 text-friet-black">{feature.title}</h3>
                    <p className="text-gray-600 leading-relaxed">{feature.description}</p>
                  </div>
                ))}
              </div>
            )}
          </div>
        </section>
      )}

      {data.id === 'special-occasions' && <FeaturedGallery />}

      {/* Packages Section */}
      {data.packages && data.id !== 'special-occasions' && data.id !== 'corporate' && (
        <section id="packages" className="py-24 bg-white">
          <div className="max-w-7xl mx-auto px-4">
            <h2 className="font-serif text-4xl text-center mb-4 text-friet-black">{data.packagesTitle}</h2>
            <p className="text-center text-gray-600 mb-16 text-xl">{data.packagesSubtitle}</p>
            <div className="grid md:grid-cols-3 gap-8">
              {data.packages.map((pkg, i) => (
                <div key={i} className={`border-2 rounded-2xl p-8 transition-all hover:shadow-2xl ${pkg.popular ? 'border-friet-gold scale-105 shadow-xl relative bg-white' : 'border-gray-100 hover:border-friet-gold'}`}>
                  {pkg.popular && (
                    <div className="absolute -top-4 left-1/2 transform -translate-x-1/2">
                      <span className="bg-friet-gold text-white px-6 py-1 rounded-full text-sm font-bold tracking-wider">
                        POPULAIR
                      </span>
                    </div>
                  )}
                  <div className="text-center mb-8">
                    {pkg.emoji && <span className="text-6xl mb-4 block">{pkg.emoji}</span>}
                    <h3 className="font-serif text-2xl font-bold text-friet-black mb-2">{pkg.name}</h3>
                    <p className="text-gray-500">{pkg.subtitle}</p>
                  </div>
                  <div className="text-center mb-8">
                    <span className="text-5xl font-black text-friet-black">{pkg.price}</span>
                    {pkg.priceNote && <span className="text-gray-500 ml-2">{pkg.priceNote}</span>}
                  </div>
                  <ul className="space-y-4 mb-10">
                    {pkg.features.map((f, fi) => (
                      <li key={fi} className="flex items-start gap-3">
                        <i className="fas fa-check text-friet-gold mt-1"></i>
                        <span className="text-gray-700">{f}</span>
                      </li>
                    ))}
                  </ul>
                  <Link to="/private-parties#contact-formulier" className={`block w-full py-4 rounded-full font-bold transition-colors text-center ${pkg.popular ? 'bg-friet-red text-white hover:bg-red-700 shadow-lg' : 'bg-friet-black text-white hover:bg-friet-gold'}`}>
                    {pkg.buttonText}
                  </Link>
                </div>
              ))}
            </div>

            {data.midnightSnack && (
              <div className="mt-16 bg-friet-black rounded-3xl p-8 md:p-12 text-white relative overflow-hidden shadow-2xl group">
                <div className="absolute top-0 right-0 w-64 h-64 bg-friet-gold/10 rounded-full -mr-20 -mt-20 group-hover:scale-110 transition-transform"></div>
                <div className="flex flex-col md:flex-row items-center justify-between relative z-10">
                  <div className="text-center md:text-left mb-8 md:mb-0">
                    <h3 className="font-serif text-3xl mb-4">{data.midnightSnack.title}</h3>
                    <p className="text-xl mb-2 opacity-90">{data.midnightSnack.description}</p>
                    <p className="text-sm opacity-60 font-light italic">{data.midnightSnack.subtext}</p>
                  </div>
                  <div className="text-center">
                    <div className="mb-4">
                      <span className="text-4xl font-bold text-friet-gold">{data.midnightSnack.price}</span>
                      <span className="text-sm opacity-70 ml-2">pp</span>
                    </div>
                    <Link to="/private-parties#contact-formulier" className="inline-block bg-white text-friet-black px-10 py-3 rounded-full font-bold hover:bg-friet-gold hover:text-white transition-all">
                      {data.midnightSnack.buttonText}
                    </Link>
                  </div>
                </div>
              </div>
            )}
          </div>
        </section>
      )}

      {/* Menu Section */}
      {data.menuSections && data.id !== 'special-occasions' && data.id !== 'corporate' && (
        <section id="menu" className="pt-4 pb-12 bg-friet-beige/20">
          <div className="max-w-6xl mx-auto px-4">
            {data.menuTitle && <h2 className="font-serif text-4xl text-center mb-16 text-friet-black">{data.menuTitle}</h2>}
            <div className="grid md:grid-cols-3 gap-8">
              {data.menuSections.map((section, i) => (
                <div key={i} className="rounded-3xl shadow-xl hover:translate-y-[-5px] transition-transform overflow-hidden">
                  {section.title === 'Slim Hamburger' ? (
                    <>
                      <div className="bg-friet-beige p-6">
                        <h3 className="font-serif text-2xl text-friet-red text-center">
                          {section.title}
                        </h3>
                      </div>
                      <div className="bg-white p-4" style={{ minHeight: 'auto' }}>
                        <ul className="space-y-2">
                          {section.items.map((item, ii) => (
                            <li key={ii} className="flex justify-between items-center group">
                              {typeof item === 'string' ? (
                                <span className="text-gray-700">{item}</span>
                              ) : (
                                <div className="flex flex-col w-full">
                                  <div className="flex justify-between items-center">
                                    <span className="text-gray-800 font-medium group-hover:text-friet-red transition-colors">
                                      {item.name}
                                    </span>
                                  </div>
                                  {item.description && <span className="text-gray-500 text-sm mt-1">{item.description}</span>}
                                </div>
                              )}
                            </li>
                          ))}
                        </ul>
                        <div className="bg-friet-beige -mx-4 px-4 py-3 mt-6 mb-4">
                          <h3 className="font-serif text-2xl text-friet-red text-center">Snacks</h3>
                        </div>
                        <ul className="space-y-2">
                          {['Frikandel', 'Boulette', 'Mexicano', 'Kippets', 'Chicken Fingers', 'Bitterbal', 'Viandel', 'Brochette', 'Kippenbrochette', 'Loempia', 'Kaaskroket', 'Vegetarische snack'].map((snack, si) => (
                            <li key={si} className="text-gray-800 font-medium">{snack}</li>
                          ))}
                        </ul>
                      </div>
                    </>
                  ) : (
                    <>
                      <div className="bg-friet-beige p-6">
                        <h3 className="font-serif text-2xl text-friet-red text-center">
                          {section.title}
                        </h3>
                      </div>
                      <div className="bg-white p-8">
                      <ul className="space-y-4">
                        {section.items.map((item, ii) => (
                          <li key={ii} className="flex justify-between items-center group">
                            {typeof item === 'string' ? (
                              <span className="text-gray-700">{item}</span>
                            ) : (
                              <>
                                <div className="flex flex-col w-full">
                                  <div className="flex justify-between items-center">
                                    <span className="text-gray-800 font-medium group-hover:text-friet-red transition-colors">
                                      {item.name} {item.bestseller && <span className="text-[10px] bg-friet-yellow/20 text-friet-red px-2 py-0.5 rounded-full ml-1">TOP</span>}
                                    </span>
                                    {item.extra && <span className="text-gray-400 text-sm font-semibold">{item.extra}</span>}
                                  </div>
                                  {item.description && <span className="text-gray-500 text-sm mt-1">{item.description}</span>}
                                </div>
                              </>
                            )}
                          </li>
                        ))}
                      </ul>
                      </div>
                    </>
                  )}
                </div>
              ))}
            </div>
          </div>
          {data.menuSpecialNote && (
            <div className="mt-12 border-t border-friet-red/10">
              <div className="w-full bg-friet-beige py-10 px-4 text-center">
                <h3 className="font-script font-bold text-friet-red text-2xl md:text-4xl mb-3 inline-block border-b border-friet-red/30 pb-1">
                  {data.menuSpecialNote.title}
                </h3>
                <p className="text-friet-oak text-base md:text-lg leading-relaxed font-serif opacity-90 whitespace-pre-line max-w-3xl mx-auto">
                  {data.menuSpecialNote.text}
                </p>
                <div className="flex justify-center mt-8">
                  <img
                    src="/images/wedding.png"
                    alt="FrietPlus Wedding"
                    className="w-full max-w-sm rounded-2xl shadow-xl object-cover"
                  />
                </div>
              </div>

              <div className="mt-16 text-center max-w-3xl mx-auto px-4">
                <h2 className="font-serif text-4xl md:text-5xl text-friet-black mb-6">
                  Een Feestje met <span className="text-friet-gold font-script ml-2"> Extra Springplezier</span>
                </h2>
                <div className="prose prose-xl max-w-none text-gray-700 leading-relaxed italic mb-10">
                  Frietplus zorgt niet enkel voor lekker eten maar ook voor extra springplezier met ons kleurrijk springkasteel. Het springkasteel wordt de dag voor of de ochtend van het feestje door ons geplaatst en de dag nadien opgehaald. Zo kan jouw kind extra lang genieten van springplezier.
                </div>
                <div className="grid grid-cols-2 gap-4 max-w-2xl mx-auto">
                  {['IMG-20250329-WA0000.jpg','IMG-20250329-WA0002.jpg','IMG-20250329-WA0003.jpg','IMG-20250329-WA0004.jpg'].map(f => (
                    <img
                      key={f}
                      src={`/images/gallery/overige/Springkasteel/${f}`}
                      alt="FrietPlus Springkasteel"
                      className="w-full rounded-xl shadow-lg object-cover aspect-square"
                    />
                  ))}
                </div>
              </div>
            </div>
          )}
        </section>
      )}

      {/* Stories Section */}
      {data.stories && data.id !== 'special-occasions' && data.id !== 'private-parties' && data.id !== 'kids' && data.id !== 'weddings' && data.id !== 'corporate' && (
        <section className="py-24 bg-white">
          <div className="max-w-7xl mx-auto px-4">
            <h2 className="font-serif text-4xl text-center mb-16 text-friet-black">Real Stories</h2>
            <div className="grid md:grid-cols-2 gap-12">
              {data.stories.map((story, i) => (
                <div key={i} className="bg-friet-beige/10 rounded-3xl p-8 md:p-12 relative">
                  <div className="absolute top-8 left-8 text-6xl text-friet-gold/10 font-serif">"</div>
                  <div className="relative z-10">
                    <p className="text-xl italic text-gray-700 mb-8 leading-relaxed">
                      {story.content}
                    </p>
                    <div className="flex items-center gap-4 border-t border-friet-gold/20 pt-8">
                      <div className="w-16 h-16 bg-friet-gold rounded-full flex items-center justify-center text-white font-serif text-2xl">
                        {story.name.charAt(0)}
                      </div>
                      <div>
                        <h4 className="font-serif text-xl font-bold">{story.name}</h4>
                        <p className="text-gray-500 text-sm mb-2">{story.location} • {story.date}</p>
                        <div className="flex flex-wrap gap-2">
                          {story.tags.map((tag, ti) => (
                            <span key={ti} className="text-[10px] uppercase tracking-wider bg-friet-gold/10 text-friet-oak px-3 py-1 rounded-full font-bold">
                              {tag}
                            </span>
                          ))}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Tasting Section */}
      {data.tastingInfo && data.id !== 'special-occasions' && data.id !== 'private-parties' && data.id !== 'kids' && data.id !== 'corporate' && (
        <section id="tasting" className="py-24 bg-friet-black text-white">
          <div className="max-w-4xl mx-auto px-4 text-center">
            <h2 className="font-serif text-4xl mb-6">{data.tastingInfo.title}</h2>
            <p className="text-xl text-friet-beige mb-12 opacity-80">{data.tastingInfo.subtitle}</p>

            <div className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-3xl p-8 md:p-12 shadow-inner">
              <ul className="grid sm:grid-cols-2 gap-6 text-left max-w-2xl mx-auto mb-12">
                {data.tastingInfo.features.map((f, i) => (
                  <li key={i} className="flex items-center gap-4">
                    <div className="w-8 h-8 rounded-full bg-friet-gold/20 flex items-center justify-center">
                      <i className="fas fa-check text-friet-gold"></i>
                    </div>
                    <span className="text-lg opacity-90">{f}</span>
                  </li>
                ))}
              </ul>
              <Link to="/private-parties#contact-formulier" className="inline-block bg-friet-red text-white px-12 py-4 rounded-full text-lg font-bold hover:bg-white hover:text-friet-red transition-all shadow-xl">
                {data.tastingInfo.buttonText}
              </Link>
            </div>
          </div>
        </section>
      )}

      {/* FAQ Section */}
      {data.faq && data.id !== 'special-occasions' && data.id !== 'private-parties' && data.id !== 'kids' && data.id !== 'corporate' && (
        <section className="py-24 bg-white">
          <div className="max-w-3xl mx-auto px-4">
            <h2 className="font-serif text-4xl text-center mb-16 text-friet-black">How it rolls</h2>
            <div className="space-y-4">
              {data.faq.map((item, i) => (
                <details key={i} className="group border-2 border-gray-100 rounded-2xl overflow-hidden hover:border-friet-gold transition-colors">
                  <summary className="p-6 font-bold text-lg cursor-pointer list-none flex justify-between items-center group-open:bg-friet-beige/10">
                    {item.question}
                    <i className="fas fa-chevron-down transition-transform group-open:rotate-180"></i>
                  </summary>
                  <div className="p-6 pt-0 text-gray-600 leading-relaxed">
                    {item.answer}
                  </div>
                </details>
              ))}
            </div>
          </div>
        </section>
      )}


      {isKids && data.storyTitle && (
        <section className="py-24 px-4 bg-white">
          <div className="max-w-4xl mx-auto text-center">
            <h2 className="font-serif text-4xl md:text-5xl text-friet-black mb-6">
              {data.storyTitle}
              <span className="text-friet-gold font-script ml-2"> {data.storySubtitle}</span>
            </h2>
            <div className="prose prose-xl max-w-none text-gray-700 leading-relaxed italic">
              {data.storyContent}
            </div>
            <div className="grid grid-cols-2 gap-4 mt-10 max-w-2xl mx-auto">
              {['IMG-20250329-WA0000.jpg','IMG-20250329-WA0002.jpg','IMG-20250329-WA0003.jpg','IMG-20250329-WA0004.jpg'].map(f => (
                <img
                  key={f}
                  src={`/images/gallery/overige/Springkasteel/${f}`}
                  alt="FrietPlus Springkasteel"
                  className="w-full rounded-xl shadow-lg object-cover aspect-square"
                />
              ))}
            </div>

          </div>
        </section>
      )}

      {data.id === 'corporate' && (
        <section className="py-24 px-4 bg-white">
          <div className="max-w-4xl mx-auto text-center">
            <h2 className="font-serif text-4xl md:text-5xl text-friet-black mb-6">
              Een Feestje met <span className="text-friet-gold font-script ml-2"> Extra Springplezier</span>
            </h2>
            <div className="prose prose-xl max-w-none text-gray-700 leading-relaxed italic mb-10">
              Frietplus zorgt niet enkel voor lekker eten maar ook voor extra springplezier met ons kleurrijk springkasteel. Het springkasteel wordt de dag voor of de ochtend van het feestje door ons geplaatst en de dag nadien opgehaald. Zo kan jouw kind extra lang genieten van springplezier.
            </div>
            <div className="grid grid-cols-2 gap-4 max-w-2xl mx-auto">
              {['IMG-20250329-WA0000.jpg','IMG-20250329-WA0002.jpg','IMG-20250329-WA0003.jpg','IMG-20250329-WA0004.jpg'].map(f => (
                <img
                  key={f}
                  src={`/images/gallery/overige/Springkasteel/${f}`}
                  alt="FrietPlus Springkasteel"
                  className="w-full rounded-xl shadow-lg object-cover aspect-square"
                />
              ))}
            </div>

          </div>
        </section>
      )}

      {data.id !== 'private-parties' && (
      <section className="py-16 bg-white">
        <div className="max-w-4xl mx-auto px-4 text-center">
          <div className="flex flex-col sm:flex-row gap-6 justify-center">
            <Link to="/private-parties#contact-formulier" className="inline-block bg-friet-red text-white px-12 py-4 rounded-full font-bold text-lg hover:bg-friet-red/90 transition-all shadow-lg">
              Neem Contact Op
            </Link>
            <Link to="/gallery" className="inline-block bg-friet-red text-white px-12 py-4 rounded-full font-bold text-lg hover:bg-friet-red/90 transition-all shadow-lg">
              Bekijk Foto's
            </Link>
            {data.id === 'corporate' && (
              <Link to="/weddings#section-plus" className="inline-block bg-friet-red text-white px-12 py-4 rounded-full font-bold text-lg hover:bg-friet-red/90 transition-all shadow-lg">
                Bekijk Menu
              </Link>
            )}
          </div>
        </div>
      </section>
      )}

      {data.id === 'private-parties' && (
        <section id="contact-formulier" className="pt-48 pb-20 bg-friet-beige">
          <div className="max-w-5xl mx-auto px-4">
            <div className="text-center mb-12">
              <h2 className="font-serif text-3xl md:text-4xl text-friet-oak">Contact/Offertevraag <span className="font-script text-friet-red">FrietPlus</span></h2>
              <p className="text-friet-oak/70 font-serif text-sm tracking-wider mt-3">
                <a href="mailto:info@frietplus.com" className="hover:text-friet-red transition-colors">info@frietplus.com</a> - <a href="tel:+32478758023" className="hover:text-friet-red transition-colors">0478/75.80.23</a>
              </p>
              <div className="w-16 h-[1px] bg-friet-oak/30 mx-auto mt-5 mb-5"></div>
              <p className="text-friet-oak/60 text-sm font-serif italic leading-relaxed max-w-lg mx-auto">
                Geef ons een belletje, stuur een mailtje of een berichtje, vul onderstaand formulier in.<br />
                We beantwoorden jouw vraag zo snel mogelijk. Beloofd.
              </p>
            </div>

            {formStatus === 'success' && (
              <div className="mb-8 bg-green-50 border border-green-200 text-green-800 px-6 py-4 rounded-lg font-serif text-sm flex items-start gap-3">
                <i className="fas fa-check-circle mt-0.5 text-green-600"></i>
                <div><strong>Bedankt!</strong> Je aanvraag is succesvol verzonden. We nemen zo snel mogelijk contact op.</div>
              </div>
            )}
            {formStatus === 'error' && (
              <div className="mb-8 bg-red-50 border border-red-200 text-red-800 px-6 py-4 rounded-lg font-serif text-sm flex items-start gap-3">
                <i className="fas fa-exclamation-circle mt-0.5 text-red-500"></i>
                <div><strong>Er ging iets mis.</strong> Probeer het opnieuw of stuur een e-mail naar <a href="mailto:info@frietplus.com" className="underline">info@frietplus.com</a>.</div>
              </div>
            )}
            <form className="grid grid-cols-1 md:grid-cols-2 gap-x-10" name="aanvraag" method="POST" data-netlify="true" data-netlify-honeypot="bot-field" onSubmit={handlePrivateFormSubmit}>
              <input type="hidden" name="form-name" value="aanvraag" />
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-serif text-friet-oak/80 mb-1.5">Naam <span className="text-friet-red">*</span></label>
                  <input name="naam" type="text" required onInvalid={setDutchValidity} onChange={clearValidity} className="w-full bg-white border border-friet-oak/20 outline-none py-3 px-3 text-base text-friet-oak placeholder-friet-oak/40 focus:border-friet-red transition-colors rounded-md" placeholder="Naam & Voornaam" />
                </div>
                <div>
                  <label className="block text-sm font-serif text-friet-oak/80 mb-1.5">E-mailadres <span className="text-friet-red">*</span></label>
                  <input name="email" type="email" required onInvalid={setDutchValidity} onChange={clearValidity} className="w-full bg-white border border-friet-oak/20 outline-none py-3 px-3 text-base text-friet-oak placeholder-friet-oak/40 focus:border-friet-red transition-colors rounded-md" placeholder="jouw@email.be" />
                </div>
                <div>
                  <label className="block text-sm font-serif text-friet-oak/80 mb-1.5">Type evenement <span className="text-friet-red">*</span></label>
                  <select name="onderwerp" required onInvalid={setDutchValidity} onChange={clearValidity} className="w-full bg-white border border-friet-oak/20 outline-none py-3 px-3 text-base text-friet-oak focus:border-friet-red transition-colors rounded-md appearance-none cursor-pointer">
                    <option value="">-- Kies een type --</option>
                    <option>Huwelijksfeest</option>
                    <option>Kinderfeestje</option>
                    <option>Privéfeest / Jubileum</option>
                    <option>Bedrijfsevent</option>
                    <option>Festival</option>
                    <option>Andere</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-serif text-friet-oak/80 mb-1.5">Telefoon <span className="text-friet-red">*</span></label>
                  <input name="telefoon" type="tel" required onInvalid={setDutchValidity} onChange={clearValidity} className="w-full bg-white border border-friet-oak/20 outline-none py-3 px-3 text-base text-friet-oak placeholder-friet-oak/40 focus:border-friet-red transition-colors rounded-md" placeholder="+32 478 75 80 23" />
                </div>
                <div>
                  <label className="block text-sm font-serif text-friet-oak/80 mb-1.5">Datum feest <span className="text-friet-red">*</span></label>
                  <input name="datum" type="date" required onInvalid={setDutchValidity} onChange={clearValidity} className="w-full bg-white border border-friet-oak/20 outline-none py-3 px-3 text-base text-friet-oak focus:border-friet-red transition-colors rounded-md" min={new Date().toISOString().split('T')[0]} />
                </div>
                <div>
                  <label className="block text-sm font-serif text-friet-oak/80 mb-1.5">Aantal gasten <span className="text-friet-red">*</span></label>
                  <input name="gasten" type="number" min="1" max="2000" required onInvalid={setDutchValidity} onChange={clearValidity} className="w-full bg-white border border-friet-oak/20 outline-none py-3 px-3 text-base text-friet-oak placeholder-friet-oak/40 focus:border-friet-red transition-colors rounded-md" placeholder="Bijv. 80" />
                </div>
              </div>

              <div className="space-y-4 mt-5 md:mt-0">
                <div>
                  <label className="block text-sm font-serif text-friet-oak/80 mb-1.5">Bericht <span className="text-friet-red">*</span></label>
                  <textarea name="bericht" rows={7} required onInvalid={setDutchValidity} onChange={clearValidity} className="w-full bg-white border border-friet-oak/20 outline-none py-3 px-3 text-base text-friet-oak placeholder-friet-oak/40 focus:border-friet-red transition-colors resize-none rounded-md" placeholder="Vertel ons meer over je feest..."></textarea>
                </div>
                <div>
                  <p className="text-sm font-serif text-friet-oak/80 mb-3">Ik heb FrietPlus ontdekt via <span className="text-friet-red">*</span></p>
                  <div className="grid grid-cols-2 sm:grid-cols-1 gap-2">
                    {['Google', 'Social media', 'Mond-aan-mond', 'Boekingsplatform', 'Andere'].map((option) => (
                      <label key={option} className="flex items-center gap-3 cursor-pointer group py-1">
                        <input type="radio" name="discovered_via" value={option} className="w-5 h-5 accent-friet-red flex-shrink-0" />
                        <span className="text-sm text-friet-oak/70 group-hover:text-friet-red transition-colors">{option}</span>
                      </label>
                    ))}
                  </div>
                </div>
              </div>

              <div className="md:col-span-2 mt-5">
                <label className="flex items-start gap-3 cursor-pointer group">
                  <input type="checkbox" required className="w-5 h-5 mt-0.5 accent-friet-red flex-shrink-0" />
                  <span className="text-sm text-friet-oak/70 group-hover:text-friet-red transition-colors">Ik ben akkoord dat FrietPlus mijn gegevens verwerkt om mijn vraag te beantwoorden.<span className="text-friet-red">*</span></span>
                </label>
              </div>

              <div className="md:col-span-2 mt-5">
                <p className="text-xs text-friet-oak/50 italic mb-6">
                  *We gebruiken je gegevens enkel om je verder te helpen met je vraag!<br />
                  We beloven dat we je geen vervelende marketingmails zullen sturen.
                </p>
                <button type="submit" disabled={formStatus === 'loading'} className="w-full sm:w-auto bg-friet-red text-white px-12 py-4 font-bold text-sm tracking-wider uppercase hover:bg-friet-black transition-all disabled:opacity-60 rounded-md">
                  {formStatus === 'loading' ? 'Bezig met verzenden...' : 'Verzenden'}
                </button>
              </div>
            </form>
          </div>
        </section>
      )}
    </div>
  );
};

export default EventPageTemplate;